/**
 * Pure projection between the renderer-agnostic `AppearanceState` (what the SVG
 * appearance panel edits) and the canonical phylocanvas-vocabulary settings bag
 * (the single source of truth both renderers read). Shared settings map to
 * phylocanvas-vocab keys both renderers honour; SVG-exclusive settings live under
 * a nested `svgExtras` the phylocanvas side ignores. Kept DOM-free and total so it
 * can be exhaustively unit-tested and so the app stays thin glue.
 */
import { type AppearanceState, mergeAppearance } from './appearance-settings';

/** SVG-exclusive fields, nested under `svgExtras` in the canonical bag. */
const SVG_EXTRA_KEYS = [
  'nodeShape',
  'fontFamily',
  'fontColour',
  'haloColour',
  'haloRadius',
  'haloWidth',
  'padding',
  'showPiecharts',
  'internalLabelSize',
  'internalLabelMin',
  'internalLabelMax',
  'scalebarDigits',
  'scalebarFontSize',
  'scalebarOpacity',
  'scalebarColour',
  'branchZoom',
  'stepZoom',
] as const;

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
}

/**
 * Project a validated `AppearanceState` into the canonical phylocanvas bag. Shared
 * settings are emitted in phylocanvas vocabulary, writing both spellings for the
 * aliased keys exactly as the phylocanvas renderer's own bag does, and the
 * SVG-exclusive settings are packed under `svgExtras`.
 */
export function appearanceToCanonical(state: AppearanceState): Record<string, unknown> {
  return {
    treeLayout: state.layout,
    showLeafLabels: state.leafLabels,
    showLabels: state.leafLabels || state.internalLabels,
    internalLabels: state.internalLabels,
    showInternalLabels: state.internalLabels,
    alignLabels: state.alignLabels,
    colourInternalEdges: state.colourEdges,
    colourEdges: state.colourEdges,
    showLeafBorders: state.leafBorders,
    leafBorders: state.leafBorders,
    scalebarVisible: state.scalebar,
    scalebarWidth: state.scalebarWidth,
    leafNodeSize: state.nodeSize,
    labelSize: state.labelSize,
    blockSize: state.blockSize,
    colorByField: state.colorByField,
    // Carry colour-by enablement so the phylocanvas side, which gates colouring on
    // `enableColorBy && colorByField`, turns colour on/off to match the panel. The
    // SVG panel models colour-by as a single select where null means off, so the
    // field's presence IS the enablement. Both phylocanvas spellings are written,
    // mirroring the phylocanvas renderer's own bag.
    colorByEnabled: state.colorByField != null,
    enableColorBy: state.colorByField != null,
    selectedMetadataFields: state.selectedMetadataFields,
    // Type-safe write partner of SVG_EXTRA_KEYS (which drives the read path).
    // Both enumerate the same SVG-exclusive fields; keep them in sync when
    // AppearanceState gains one. The write side stays an explicit literal
    // because `state[key]` would require an index signature on AppearanceState.
    svgExtras: {
      nodeShape: state.nodeShape,
      fontFamily: state.fontFamily,
      fontColour: state.fontColour,
      haloColour: state.haloColour,
      haloRadius: state.haloRadius,
      haloWidth: state.haloWidth,
      padding: state.padding,
      showPiecharts: state.showPiecharts,
      internalLabelSize: state.internalLabelSize,
      internalLabelMin: state.internalLabelMin,
      internalLabelMax: state.internalLabelMax,
      scalebarDigits: state.scalebarDigits,
      scalebarFontSize: state.scalebarFontSize,
      scalebarOpacity: state.scalebarOpacity,
      scalebarColour: state.scalebarColour,
      branchZoom: state.branchZoom,
      stepZoom: state.stepZoom,
    },
  };
}

/**
 * Read the canonical bag back into a validated `AppearanceState`. Shared keys are
 * read from phylocanvas vocabulary (preferring the explicit leaf/internal spellings
 * the phylocanvas renderer writes, falling back to the dialog's `showLabels`-as-leaf);
 * SVG-exclusive keys are read from `svgExtras`. Everything passes through
 * `mergeAppearance`, so absent or invalid values fall back to the defaults.
 */
export function canonicalToAppearance(
  bag: Record<string, unknown>,
  available?: readonly string[],
): AppearanceState {
  const extras = asRecord(bag.svgExtras);
  const raw: Record<string, unknown> = {
    layout: bag.treeLayout,
    leafLabels: bag.showLeafLabels !== undefined ? bag.showLeafLabels : bag.showLabels,
    internalLabels:
      bag.showInternalLabels !== undefined ? bag.showInternalLabels : bag.internalLabels,
    alignLabels: bag.alignLabels,
    colourEdges:
      bag.colourInternalEdges !== undefined ? bag.colourInternalEdges : bag.colourEdges,
    leafBorders: bag.showLeafBorders !== undefined ? bag.showLeafBorders : bag.leafBorders,
    scalebar: bag.scalebarVisible,
    scalebarWidth: bag.scalebarWidth,
    nodeSize: bag.leafNodeSize,
    labelSize: bag.labelSize,
    blockSize: bag.blockSize,
    // A remembered field with colour-by explicitly disabled reads back as null: the
    // SVG panel has no "remember while off" state, so off means no field. An absent
    // enablement flag (e.g. an older SVG-only save) keeps the field.
    colorByField:
      (bag.enableColorBy ?? bag.colorByEnabled) === false ? null : bag.colorByField,
    selectedMetadataFields: bag.selectedMetadataFields,
  };
  for (const key of SVG_EXTRA_KEYS) {
    raw[key] = extras[key];
  }
  return mergeAppearance(raw, available);
}

/**
 * Whether the canonical bag carries any persisted setting. Drives the panel's
 * "don't perturb a never-customised renderer" gate: an empty bag means the user
 * never saved anything, so the renderer keeps its own byte-identical defaults.
 */
export function canonicalHasAppearance(bag: Record<string, unknown>): boolean {
  return Object.keys(asRecord(bag)).length > 0;
}

/**
 * One-time migration: fold a legacy standalone `appearance` bag into the canonical
 * `phylocanvas` bag and drop the legacy key. Returns the rewritten full settings
 * blob, or `null` when there is nothing to migrate (so the caller can skip the
 * write). Pure: the caller owns persistence.
 */
export function foldLegacyAppearance(
  settings: Record<string, unknown>,
): Record<string, unknown> | null {
  if (settings.appearance == null) return null;
  const canonical = appearanceToCanonical(mergeAppearance(settings.appearance));
  const next: Record<string, unknown> = {
    ...settings,
    phylocanvas: { ...asRecord(settings.phylocanvas), ...canonical },
  };
  delete next.appearance;
  return next;
}

/** Legacy top-level colour-by keys an older build wrote outside the canonical bag. */
const LEGACY_COLOR_BY_KEYS = ['colorByField', 'colorByEnabled', 'colorByPalette'] as const;

/**
 * One-time migration: fold legacy top-level colour-by keys (written by an older
 * build's phylocanvas live controls) into the canonical `phylocanvas` bag and drop
 * them. The canonical bag wins on conflict, so a value already migrated, or one the
 * SVG panel wrote, is never clobbered; stale top-level keys are removed either way so
 * there is a single source of truth going forward. Returns the rewritten settings, or
 * `null` when there is nothing to migrate. Pure: the caller owns persistence.
 */
export function foldLegacyColorBy(
  settings: Record<string, unknown>,
): Record<string, unknown> | null {
  const present = LEGACY_COLOR_BY_KEYS.filter((key) => key in settings);
  if (present.length === 0) return null;
  const phylocanvas = { ...asRecord(settings.phylocanvas) };
  for (const key of present) {
    if (phylocanvas[key] === undefined) phylocanvas[key] = settings[key];
  }
  const next: Record<string, unknown> = { ...settings, phylocanvas };
  for (const key of present) delete next[key];
  return next;
}
